// src/Components/HighLevelComponents/Tool/index.tsx
import React, { useState, useContext, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { HashLoader } from "react-spinners";
import { BsFillArrowRightCircleFill } from "react-icons/bs";
import { GoogleAnalyticsContext } from "../../../Hooks/GoogleAnalyticsContext";
import useCanCreateQuiz from "../../../Hooks/useCanCreateQuiz";
import { toast } from "react-hot-toast";
import { useFirebaseAuth } from "../../../Hooks/FirebaseAuth";
import { YippityUserContext } from "../../../Hooks/YippityUserContext";
import ReactFlagsSelect from "react-flags-select";
import untruncateJson from "untruncate-json";
import ToolResults from "./ToolResults";
import { ToolConfig } from "./types";
import DocumentUploader from "../DocumentUploader/DocumentUploader";

// Map country codes to languages
const countryToLanguage: { [countryCode: string]: string } = {
  GB: "English",
  IT: "Italiano",
  ES: "Español",
  FR: "Français",
  DE: "Deutsch",
  PT: "Português",
  CN: "中文",
  SA: "العربية",
};

// Difficulty options with descriptive labels
const difficultyOptions = [
  { value: "easy", label: "Basic" },
  { value: "medium", label: "Intermediate" },
  { value: "hard", label: "Advanced" },
];

// Count options with descriptive labels
const countOptions = [
  { value: 5, label: "5 items" },
  { value: 10, label: "10 items" },
  { value: 15, label: "15 items" },
  { value: 20, label: "20 items" },
  { value: 25, label: "25 items" },
  { value: 30, label: "30 items" },
  { value: 35, label: "35 items" },
  { value: 40, label: "40 items" },
];

interface ToolProps {
  config: ToolConfig;
  executeEndpoint: string;
}

const Tool: React.FC<ToolProps> = ({ config, executeEndpoint }) => {
  const navigate = useNavigate();
  const { logEvent } = useContext(GoogleAnalyticsContext);
  const canCreateQuiz = useCanCreateQuiz();
  const { refetch } = useContext(YippityUserContext);
  const firebaseAuth = useFirebaseAuth();

  const [inputValues, setInputValues] = useState<Record<string, string>>({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [results, setResults] = useState<any[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [activeInputMode, setActiveInputMode] = useState<
    "text" | "url" | "document"
  >("text");
  const [urlInput, setUrlInput] = useState<string>("");
  const [documentContent, setDocumentContent] = useState<string>("");
  const [countryCode, setCountryCode] = useState<string>(() => {
    // Initialize from localStorage or default to English
    return localStorage.getItem("preferredLanguage") || "GB";
  });
  const [difficulty, setDifficulty] = useState<string>("medium");
  const [count, setCount] = useState<number>(10);

  const resultContainerRef = useRef<HTMLDivElement>(null);

  // Find text input
  const textInput = config.inputs.find((input) => input.type === "textarea");

  useEffect(() => {
    // Initialize input values
    const initialValues: Record<string, string> = {};
    config.inputs.forEach((input) => {
      initialValues[input.name] = "";
    });
    setInputValues(initialValues);
  }, [config.inputs]);

  const handleInputChange = (name: string, value: string) => {
    setInputValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle document content extraction
  const handleDocumentContentExtracted = (content: string) => {
    setDocumentContent(content);
    if (textInput) {
      handleInputChange(textInput.name, content);
    }
    logEvent(
      "ToolAction",
      "DocumentExtracted",
      `${config.id}_${content.length}chars`
    );
  };

  // Log language changes
  useEffect(() => {
    if (countryCode !== "GB") {
      logEvent("Language", "Change", countryToLanguage[countryCode]);
    }
  }, [countryCode, logEvent]);

  // Create a memoized handler for country selection to prevent infinite loops
  const handleCountrySelect = React.useCallback((code: string) => {
    setCountryCode(code);
  }, []);

  // Update localStorage when language changes
  useEffect(() => {
    localStorage.setItem("preferredLanguage", countryCode);
  }, [countryCode]);

  const validateInputs = (): boolean => {
    if (activeInputMode === "text" && textInput) {
      if (!inputValues[textInput.name]) {
        toast.error(`${textInput.label} is required`);
        return false;
      }
    } else if (activeInputMode === "url") {
      if (!urlInput) {
        toast.error("URL is required");
        return false;
      }

      // Basic URL validation
      try {
        new URL(urlInput);
      } catch (e) {
        toast.error("Please enter a valid URL");
        return false;
      }
    } else if (activeInputMode === "document") {
      if (!documentContent) {
        toast.error("Please upload a document");
        return false;
      }
    }
    return true;
  };

  const handleGenerate = async (streaming = true) => {
    if (!canCreateQuiz) {
      return navigate("/pricing");
    }

    if (!validateInputs()) {
      return;
    }

    setIsGenerating(true);
    setResults([]);
    logEvent("Tool", streaming ? "GenerateStreaming" : "Generate", config.id);

    try {
      // Prepare inputs based on active input mode
      const payload: Record<string, any> = {
        // Add count and difficulty parameters
        count: count.toString(),
        difficulty: difficulty,
      };

      if (activeInputMode === "text" && textInput) {
        payload[textInput.name] = inputValues[textInput.name];
      } else if (activeInputMode === "url") {
        payload["url"] = urlInput;
      } else if (activeInputMode === "document" && textInput) {
        payload[textInput.name] = documentContent;
      }

      // Add language
      payload["language"] = countryToLanguage[countryCode];

      // Get token for authentication
      let token = "";
      try {
        if (firebaseAuth) {
          token = await firebaseAuth.getIdToken();
        } else {
          toast.error("Authentication failed. Please sign in again.");
          navigate("/signin");
          return;
        }
      } catch (error) {
        console.error("Error getting token:", error);
        toast.error("Authentication failed. Please sign in again.");
        navigate("/signin");
        return;
      }

      if (streaming) {
        await handleStreamingRequest(token, payload);
      } else {
        await handleStandardRequest(token, payload);
      }

      await refetch();
    } catch (error) {
      console.error("Error generating:", error);
      toast.error("Failed to generate. Please try again.");
    } finally {
      setIsGenerating(false);
      setIsStreaming(false);
    }
  };

  const handleStandardRequest = async (
    token: string,
    payload: Record<string, any>
  ) => {
    const response = await fetch(
      `${executeEndpoint}/${config.promptId}/execute`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          inputs: payload,
        }),
      }
    );

    if (!response.ok) {
      throw new Error(`Error: ${response.status}`);
    }

    const data = await response.json();
    setResults(data);
  };

  const handleStreamingRequest = async (
    token: string,
    payload: Record<string, any>
  ) => {
    setIsStreaming(true);

    const response = await fetch(
      `${executeEndpoint}/${config.promptId}/stream`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          inputs: payload,
        }),
      }
    );

    if (!response.ok) {
      throw new Error(`Error: ${response.status}`);
    }

    // Handle streaming response
    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error("Unable to read stream");
    }

    const decoder = new TextDecoder();
    let buffer = "";

    // Read the stream
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      // Decode the chunk and add to buffer
      buffer += decoder.decode(value, { stream: true });
      try {
        // Try to parse the buffer as JSON
        const parsedBuffer = JSON.parse(untruncateJson(buffer));
        if (parsedBuffer.results) {
          setResults(parsedBuffer.results);
        }
      } catch (e) {
        // If we can't parse yet, continue reading
        console.log("Parsing error (expected during streaming):", e);
      }
    }
  };

  const resetTool = () => {
    setResults([]);
    // Don't reset input values to allow for quick adjustments and regeneration
  };

  // Scroll to results when they're available
  useEffect(() => {
    if (results.length > 0 && resultContainerRef.current) {
      resultContainerRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [results]);

  return (
    <div className="flex flex-col w-full max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-3">
          {config.title}
        </h1>
        <p className="text-gray-600">{config.description}</p>
      </div>

      {/* Input Section */}
      <div className="bg-white p-6 rounded-lg shadow-sm mb-8">
        <h2 className="text-xl font-semibold mb-4">Input</h2>

        {/* Input Type Tabs */}
        <div className="flex mb-4 border-b border-gray-200">
          <button
            className={`py-2 px-4 font-medium ${
              activeInputMode === "text"
                ? "text-yellow-500 border-b-2 border-yellow-500"
                : "text-gray-500 hover:text-gray-700"
            }`}
            onClick={() => setActiveInputMode("text")}
          >
            Text Input
          </button>
          <button
            className={`py-2 px-4 font-medium ${
              activeInputMode === "url"
                ? "text-yellow-500 border-b-2 border-yellow-500"
                : "text-gray-500 hover:text-gray-700"
            }`}
            onClick={() => setActiveInputMode("url")}
          >
            URL Input
          </button>
          <button
            className={`py-2 px-4 font-medium ${
              activeInputMode === "document"
                ? "text-yellow-500 border-b-2 border-yellow-500"
                : "text-gray-500 hover:text-gray-700"
            }`}
            onClick={() => {
              setActiveInputMode("document");
              logEvent(
                "ToolAction",
                "SwitchInputMode",
                `${config.id}_document`
              );
            }}
          >
            Document Input
          </button>
        </div>

        {/* Control Panel - Language, Count, and Difficulty */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* Language Selection */}
          <div>
            <label className="font-medium text-gray-700 mb-1 block">
              Language
            </label>
            <ReactFlagsSelect
              countries={Object.keys(countryToLanguage)}
              customLabels={countryToLanguage}
              selected={countryCode}
              onSelect={setCountryCode}
              selectButtonClassName="w-full p-2 border border-gray-300 rounded-md"
            />
          </div>

          {/* Count Selection */}
          <div>
            <label className="font-medium text-gray-700 mb-1 block">
              Number of Results
            </label>
            <select
              value={count}
              onChange={(e) => setCount(parseInt(e.target.value))}
              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
            >
              {countOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Difficulty Selection */}
          <div>
            <label className="font-medium text-gray-700 mb-1 block">
              Difficulty Level
            </label>
            <select
              value={difficulty}
              onChange={(e) => setDifficulty(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
            >
              {difficultyOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Text Input */}
        {activeInputMode === "text" && textInput && (
          <div className="flex flex-col">
            <label className="font-medium text-gray-700 mb-1">
              {textInput.label} <span className="text-red-500">*</span>
            </label>
            {textInput.description && (
              <p className="text-sm text-gray-500 mb-2">
                {textInput.description}
              </p>
            )}
            <textarea
              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
              placeholder={textInput.placeholder}
              value={inputValues[textInput.name] || ""}
              onChange={(e) =>
                handleInputChange(textInput.name, e.target.value)
              }
              rows={8}
            />
          </div>
        )}

        {/* URL Input */}
        {activeInputMode === "url" && (
          <div className="flex flex-col">
            <label className="font-medium text-gray-700 mb-1">
              Source URL <span className="text-red-500">*</span>
            </label>
            <p className="text-sm text-gray-500 mb-2">
              Enter a URL with content you want to generate from (we'll crawl
              and process the content)
            </p>
            <div className="flex relative">
              <input
                type="url"
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-400 focus:border-transparent pr-10"
                placeholder="https://en.wikipedia.org/wiki/Vietnam_War"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
              />
              {urlInput && (
                <button
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-yellow-500"
                  onClick={() => handleGenerate(true)}
                  disabled={isGenerating}
                >
                  <BsFillArrowRightCircleFill size={20} />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Document Input */}
        {activeInputMode === "document" && (
          <div className="flex flex-col">
            <label className="font-medium text-gray-700 mb-1">
              Upload Document <span className="text-red-500">*</span>
            </label>
            <p className="text-sm text-gray-500 mb-2">
              Upload a document to extract content from (PDF, DOCX, TXT, CSV,
              XLSX)
            </p>
            <DocumentUploader
              onContentExtracted={handleDocumentContentExtracted}
              isLoading={isGenerating}
            />
            {documentContent && (
              <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-md">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">
                    Document content extracted:
                  </span>{" "}
                  {documentContent.length} characters
                </p>
              </div>
            )}
          </div>
        )}

        <div className="mt-6 flex justify-end space-x-4">
          <button
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
            onClick={resetTool}
            disabled={isGenerating || results.length === 0}
          >
            Reset
          </button>
          <button
            className="px-4 py-2 bg-yellow-400 text-gray-800 rounded-md hover:bg-yellow-500 transition-colors flex items-center"
            onClick={() => handleGenerate(true)}
            disabled={isGenerating}
          >
            {isGenerating ? (
              <>
                <HashLoader size={18} color="#4B5563" className="mr-2" />
                <span>Generating...</span>
              </>
            ) : (
              "Generate"
            )}
          </button>
        </div>
      </div>

      {/* Results Section */}
      <div ref={resultContainerRef}>
        {(isGenerating || results.length > 0) && (
          <ToolResults
            title={config.title}
            isGenerating={isGenerating}
            isStreaming={isStreaming}
            results={results}
            ResultRenderer={config.resultRenderer}
            toolId={config.id}
          />
        )}
      </div>
    </div>
  );
};

export default Tool;
